import {
  NOTIFICATION_KINDS,
  NOTIFICATION_KIND_DESCRIPTIONS,
  NOTIFICATION_KIND_LABELS,
  NOTIFICATION_SEVERITIES,
  NOTIFICATION_STATUS_FILTERS,
  WEBHOOK_EVENTS,
  WEBHOOK_EVENT_LABELS,
  WEBHOOK_SIGNATURE_HEADER,
  WEBHOOK_TIMESTAMP_HEADER,
  roleHas,
  type NotificationDto,
  type NotificationKind,
  type NotificationPrefsDto,
  type WebhookDeliveryDto,
  type WebhookDto,
  type WebhookEvent,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Bell,
  Check,
  CheckCheck,
  ExternalLink,
  Info,
  Plus,
  Send,
  Settings2,
  Trash2,
  Webhook,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ClientFilter } from "@/components/client-filter";
import { SecretBox } from "@/components/secret-box";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errorMessage } from "@/lib/api";
import { api7, notificationsKey, queries7 } from "@/lib/fase7-api";
import { queries } from "@/lib/queries";
import { cn, formatDate, formatRelative } from "@/lib/utils";

const ACCOUNT_QUALITY_URL = "https://business.facebook.com/accountquality";
const ALL = "__todos__";

export const Route = createFileRoute("/app/$workspaceId/notificacoes")({
  component: NotificacoesPage,
});

const STATUS_LABELS: Record<(typeof NOTIFICATION_STATUS_FILTERS)[number], string> = {
  todas: "Todas",
  nao_lidas: "Não lidas",
  lidas: "Lidas",
  arquivadas: "Arquivadas",
};
const SEVERITY_LABELS = { info: "Informação", aviso: "Aviso", erro: "Erro" } as const;

function NotificacoesPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));
  const canManage = workspace.data ? roleHas(workspace.data.role, "alerts:manage") : false;
  const [aba, setAba] = useState("alertas");
  return (
    <>
      <PageHeader
        title="Central de alertas"
        description="Conta desativada, pagamento, limite de gasto, anúncio reprovado, gasto acima do esperado, CPA acima da meta, token expirado, falha de sincronização, regra executada e novo lead. Preferências por tipo e webhooks de saída."
      />
      <Tabs value={aba} onValueChange={setAba}>
        <TabsList>
          <TabsTrigger value="alertas"><Bell className="size-4" /> Alertas</TabsTrigger>
          <TabsTrigger value="preferencias"><Settings2 className="size-4" /> Preferências</TabsTrigger>
          {canManage ? <TabsTrigger value="webhooks"><Webhook className="size-4" /> Webhooks</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="alertas"><AlertsTab workspaceId={workspaceId} /></TabsContent>
        <TabsContent value="preferencias"><PrefsTab workspaceId={workspaceId} /></TabsContent>
        {canManage ? <TabsContent value="webhooks"><WebhooksTab workspaceId={workspaceId} /></TabsContent> : null}
      </Tabs>
    </>
  );
}

// ---- Alertas ---------------------------------------------------------------------------------------

function severityIcon(severity: NotificationDto["severity"]) {
  if (severity === "erro") return <XCircle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />;
  if (severity === "aviso") return <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />;
  return <Info className="mt-0.5 size-5 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />;
}

function NotificationLink({ n, workspaceId }: { n: NotificationDto; workspaceId: string }) {
  const data = n.data ?? {};
  switch (n.kind) {
    case "conta_desativada":
    case "pagamento_falhou":
    case "limite_proximo":
      return <Link to="/app/$workspaceId/contas-de-anuncio" params={{ workspaceId }} className="underline">ver contas</Link>;
    case "anuncio_reprovado":
      return (
        <Link to="/app/$workspaceId/campanhas" params={{ workspaceId }} search={{ ...(typeof data.adAccountMetaId === "string" ? { conta: data.adAccountMetaId } : {}), aba: "ads" }} className="underline">
          ver anúncios
        </Link>
      );
    case "gasto_acima_esperado":
    case "cpa_acima_meta":
      return <Link to="/app/$workspaceId/relatorios" params={{ workspaceId }} search={n.clientId ? { cliente: n.clientId } : {}} className="underline">ver relatório</Link>;
    case "token_expirado":
      return <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }} className="underline">reconectar</Link>;
    case "regra_executada":
      return <Link to="/app/$workspaceId/regras" params={{ workspaceId }} search={typeof data.runId === "string" ? { execucao: data.runId } : {}} className="underline">ver execução</Link>;
    case "novo_lead":
      return <Link to="/app/$workspaceId/leads" params={{ workspaceId }} search={typeof data.leadId === "string" ? { lead: data.leadId } : {}} className="underline">ver lead</Link>;
    default:
      return null;
  }
}

function AlertsTab({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<(typeof NOTIFICATION_STATUS_FILTERS)[number]>("todas");
  const [kind, setKind] = useState(ALL);
  const [severity, setSeverity] = useState(ALL);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [q, setQ] = useState("");
  const params = useMemo(
    () => ({ status, kind: kind === ALL ? undefined : kind, severity: severity === ALL ? undefined : severity, clientId, q: q || undefined, limit: "100" }),
    [status, kind, severity, clientId, q],
  );
  const notifications = useQuery(queries7.notifications(workspaceId, params));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationsKey(workspaceId) });
  const after = (ok?: string) => ({
    onSuccess: async () => {
      await invalidate();
      if (ok) toast.success(ok);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const readAll = useMutation({ mutationFn: () => api7.readAll(workspaceId), ...after("Todas marcadas como lidas.") });
  const read = useMutation({ mutationFn: (id: string) => api7.read(workspaceId, [id]), ...after() });
  const unread = useMutation({ mutationFn: (id: string) => api7.unread(workspaceId, id), ...after() });
  const archive = useMutation({ mutationFn: (id: string) => api7.archive(workspaceId, [id]), ...after("Arquivado.") });
  const unarchive = useMutation({ mutationFn: (id: string) => api7.unarchive(workspaceId, [id]), ...after("Desarquivado.") });
  const total = notifications.data?.unread ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="sm:w-40" aria-label="Status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {NOTIFICATION_STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="sm:w-56" aria-label="Tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os tipos</SelectItem>
            {NOTIFICATION_KINDS.map((k) => (
              <SelectItem key={k} value={k}>{NOTIFICATION_KIND_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="sm:w-40" aria-label="Severidade"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Qualquer severidade</SelectItem>
            {NOTIFICATION_SEVERITIES.map((s) => (
              <SelectItem key={s} value={s}>{SEVERITY_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
        <Input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:w-48" />
        {total > 0 ? (
          <Button variant="outline" className="sm:ml-auto" onClick={() => readAll.mutate()} loading={readAll.isPending}>
            <CheckCheck /> Marcar todas como lidas ({total})
          </Button>
        ) : null}
      </div>
      <Card>
        <CardContent>
          {notifications.isPending ? (
            <LoadingState rows={3} />
          ) : notifications.isError ? (
            <ErrorState error={notifications.error} onRetry={() => void notifications.refetch()} />
          ) : notifications.data.items.length === 0 ? (
            <EmptyState icon={<Bell className="size-8" aria-hidden />} title="Nenhum alerta" description="Nada por aqui com esses filtros. Os alertas são detectados a cada sincronização." />
          ) : (
            <ul className="divide-y">
              {notifications.data.items.map((n) => (
                <li key={n.id} className={cn("flex gap-3 py-4", n.readAt ? "opacity-70" : undefined)}>
                  {severityIcon(n.severity)}
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{n.title}</p>
                      <Badge variant="outline">{NOTIFICATION_KIND_LABELS[n.kind]}</Badge>
                      {!n.readAt ? <Badge variant="warning">Nova</Badge> : null}
                      {n.resolvedAt ? <Badge variant="success">Resolvido</Badge> : null}
                      {n.archivedAt ? <Badge variant="secondary">Arquivado</Badge> : null}
                    </div>
                    <p className="text-sm text-muted-foreground">{n.message}</p>
                    <p className="text-xs text-muted-foreground">
                      <span title={formatDate(n.createdAt)}>{formatRelative(n.createdAt)}</span>
                      {n.kind === "conta_desativada" || n.kind === "anuncio_reprovado" ? (
                        <>
                          {" · "}
                          <a href={ACCOUNT_QUALITY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
                            Qualidade da conta <ExternalLink className="size-3" aria-hidden />
                          </a>
                        </>
                      ) : null}
                      {" · "}
                      <NotificationLink n={n} workspaceId={workspaceId} />
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
                    {n.readAt ? (
                      <Button variant="ghost" size="sm" onClick={() => unread.mutate(n.id)} aria-label="Marcar como não lida">
                        Não lida
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => read.mutate(n.id)} loading={read.isPending && read.variables === n.id}>
                        <Check /> Lida
                      </Button>
                    )}
                    {n.archivedAt ? (
                      <Button variant="ghost" size="icon" aria-label="Desarquivar" onClick={() => unarchive.mutate(n.id)}>
                        <ArchiveRestore />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" aria-label="Arquivar" onClick={() => archive.mutate(n.id)}>
                        <Archive />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Preferências ----------------------------------------------------------------------------------

function PrefsTab({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const prefs = useQuery(queries7.notificationPrefs(workspaceId));
  const save = useMutation({
    mutationFn: (body: Parameters<typeof api7.savePrefs>[1]) => api7.savePrefs(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...notificationsKey(workspaceId), "prefs"] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  if (prefs.isPending) return <LoadingState rows={4} />;
  if (prefs.isError) return <ErrorState error={prefs.error} onRetry={() => void prefs.refetch()} />;
  const p: NotificationPrefsDto = prefs.data;
  const setKind = (kind: NotificationKind, channel: "app" | "email", value: boolean) =>
    save.mutate({ kinds: { [kind]: { ...p.kinds[kind], [channel]: value } } });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Suas preferências neste workspace</CardTitle>
        <CardDescription>
          "App" mostra o alerta na central e no sininho. "E-mail" manda na hora para o seu e-mail. Com o resumo diário ligado, os e-mails individuais param e você recebe um só, às 8h (horário de Brasília), com o que ficou sem ler.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <input type="checkbox" checked={p.dailyDigest} onChange={(e) => save.mutate({ dailyDigest: e.target.checked })} />
          Resumo diário por e-mail (8h) em vez de e-mails na hora
        </label>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2 text-center">App</th>
                <th className="px-3 py-2 text-center">E-mail</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {NOTIFICATION_KINDS.map((k) => (
                <tr key={k}>
                  <td className="px-3 py-2">
                    <p className="font-medium">{NOTIFICATION_KIND_LABELS[k]}</p>
                    <p className="text-xs text-muted-foreground">{NOTIFICATION_KIND_DESCRIPTIONS[k]}</p>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" aria-label={`${NOTIFICATION_KIND_LABELS[k]} no app`} checked={p.kinds[k].app} onChange={(e) => setKind(k, "app", e.target.checked)} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" aria-label={`${NOTIFICATION_KIND_LABELS[k]} por e-mail`} checked={p.kinds[k].email} onChange={(e) => setKind(k, "email", e.target.checked)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">Webhooks são do workspace (aba Webhooks, Dono/Admin) e recebem todos os tipos que assinarem, independentemente destas preferências.</p>
      </CardContent>
    </Card>
  );
}

// ---- Webhooks --------------------------------------------------------------------------------------

function WebhooksTab({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const hooks = useQuery(queries7.webhooks(workspaceId));
  const [novo, setNovo] = useState(false);
  const [created, setCreated] = useState<WebhookDto | null>(null);
  const [deliveriesOf, setDeliveriesOf] = useState<WebhookDto | null>(null);
  const [remove, setRemove] = useState<WebhookDto | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...notificationsKey(workspaceId), "webhooks"] });
  const test = useMutation({
    mutationFn: (id: string) => api7.testWebhook(workspaceId, id),
    onSuccess: async (d) => {
      await invalidate();
      if (d.status === "ok") toast.success(`Entregue: o destino respondeu ${d.responseStatus ?? ""}.`);
      else toast.error(`Falhou: ${d.error ?? "sem resposta"}.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const toggle = useMutation({
    mutationFn: (h: WebhookDto) => api7.updateWebhook(workspaceId, h.id, { active: !h.active }),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api7.deleteWebhook(workspaceId, id),
    onSuccess: async () => {
      await invalidate();
      setRemove(null);
      toast.success("Webhook excluído.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Cada evento é um POST JSON assinado com HMAC-SHA256 no cabeçalho <code>{WEBHOOK_SIGNATURE_HEADER}</code> (sobre <code>{WEBHOOK_TIMESTAMP_HEADER}.corpo</code>). Falhas são reenviadas com backoff (30 s, 2 min, 8 min, 32 min, 1 h).
        </p>
        <Button onClick={() => setNovo(true)}>
          <Plus /> Novo webhook
        </Button>
      </div>
      <Card>
        <CardContent>
          {hooks.isPending ? (
            <LoadingState rows={2} />
          ) : hooks.isError ? (
            <ErrorState error={hooks.error} onRetry={() => void hooks.refetch()} />
          ) : hooks.data.length === 0 ? (
            <EmptyState icon={<Webhook className="size-8" aria-hidden />} title="Nenhum webhook" description="Cadastre a URL do seu sistema (CRM, Slack via integração, n8n, Make…) para receber os alertas e os leads." />
          ) : (
            <ul className="divide-y">
              {hooks.data.map((h) => (
                <li key={h.id} className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{h.name}</p>
                      <Badge variant={h.active ? "success" : "secondary"}>{h.active ? "ativo" : "pausado"}</Badge>
                      {h.lastDeliveryStatus ? (
                        <Badge variant={h.lastDeliveryStatus === "ok" ? "success" : h.lastDeliveryStatus === "erro" ? "destructive" : "warning"}>
                          última entrega: {h.lastDeliveryStatus}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="truncate text-sm text-muted-foreground">{h.url}</p>
                    <p className="text-xs text-muted-foreground">
                      {h.events.length === 0 ? "Todos os eventos" : h.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(", ")}
                      {h.lastDeliveryAt ? ` · ${formatRelative(h.lastDeliveryAt)}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => test.mutate(h.id)} loading={test.isPending && test.variables === h.id}>
                      <Send /> Testar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeliveriesOf(h)}>Entregas</Button>
                    <Button variant="ghost" size="sm" onClick={() => toggle.mutate(h)}>{h.active ? "Pausar" : "Ativar"}</Button>
                    <Button variant="ghost" size="icon" aria-label="Excluir" onClick={() => setRemove(h)}>
                      <Trash2 />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <WebhookDialog
        workspaceId={workspaceId}
        open={novo}
        onOpenChange={setNovo}
        onCreated={(h) => {
          setCreated(h);
          void invalidate();
        }}
      />
      <Dialog open={created !== null} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook criado</DialogTitle>
            <DialogDescription>Guarde o segredo agora: ele não será mostrado de novo. Use-o para conferir a assinatura de cada entrega.</DialogDescription>
          </DialogHeader>
          <SecretBox secret={created?.secret ?? ""} />
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Entendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeliveriesDialog workspaceId={workspaceId} hook={deliveriesOf} onOpenChange={(o) => !o && setDeliveriesOf(null)} />
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={(o) => !o && setRemove(null)}
        title="Excluir o webhook?"
        description="As entregas pendentes param e o log é apagado."
        confirmLabel="Excluir"
        destructive
        loading={del.isPending}
        onConfirm={() => remove && del.mutate(remove.id)}
      />
    </div>
  );
}

function WebhookDialog({ workspaceId, open, onOpenChange, onCreated }: { workspaceId: string; open: boolean; onOpenChange: (o: boolean) => void; onCreated: (h: WebhookDto) => void }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const create = useMutation({
    mutationFn: () => api7.createWebhook(workspaceId, { name, url, events }),
    onSuccess: (h) => {
      onOpenChange(false);
      setName("");
      setUrl("");
      setEvents([]);
      onCreated(h);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo webhook</DialogTitle>
          <DialogDescription>URL https do seu sistema. Sem eventos marcados, recebe todos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormField id="wh-nome" label="Nome">
            <Input id="wh-nome" value={name} onChange={(e) => setName(e.target.value)} placeholder="CRM da agência" />
          </FormField>
          <FormField id="wh-url" label="URL">
            <Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </FormField>
          <div className="space-y-1">
            <p className="text-sm font-medium">Eventos</p>
            <div className="grid max-h-56 gap-1 overflow-y-auto sm:grid-cols-2">
              {WEBHOOK_EVENTS.map((e) => (
                <label key={e} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={events.includes(e)} onChange={(ev) => setEvents(ev.target.checked ? [...events, e] : events.filter((x) => x !== e))} />
                  {WEBHOOK_EVENT_LABELS[e]}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDialog({ workspaceId, hook, onOpenChange }: { workspaceId: string; hook: WebhookDto | null; onOpenChange: (o: boolean) => void }) {
  const deliveries = useQuery(queries7.webhookDeliveries(workspaceId, hook?.id ?? ""));
  const badge = (d: WebhookDeliveryDto) => (d.status === "ok" ? "success" : d.status === "erro" ? "destructive" : "warning");
  return (
    <Dialog open={hook !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Entregas · {hook?.name}</DialogTitle>
          <DialogDescription>Últimas 30 entregas, com tentativas e resposta do destino.</DialogDescription>
        </DialogHeader>
        {deliveries.isPending ? (
          <LoadingState rows={3} />
        ) : deliveries.isError ? (
          <ErrorState error={deliveries.error} />
        ) : deliveries.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma entrega ainda. Use "Testar".</p>
        ) : (
          <ul className="divide-y text-sm">
            {deliveries.data.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 py-2">
                <Badge variant={badge(d)}>{d.status}</Badge>
                <span className="font-medium">{WEBHOOK_EVENT_LABELS[d.event]}</span>
                <span className="text-muted-foreground">
                  {d.attempts} tentativa(s){d.responseStatus ? ` · HTTP ${d.responseStatus}` : ""}{d.nextAttemptAt ? ` · próxima ${formatRelative(d.nextAttemptAt)}` : ""} · {formatDate(d.createdAt)}
                </span>
                {d.error ? <span className="w-full text-xs text-destructive">{d.error}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
